import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { toast } from "react-toastify";
import { Pencil, ArrowLeft } from "lucide-react";

function GenericEditForm({ title, endpoint, backTo, fields }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    api
      .get(`${endpoint}/${id}`)
      .then((res) => setFormData(res.data))
      .catch((err) => {
        console.error(err);
        setErrors({ general: `Error al cargar la ${title.toLowerCase()}` });
      });
  }, [id, endpoint, title]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Frontend validation
    let newErrors = {};
    fields.forEach((field) => {
      if (field.required && !formData[field.name]?.trim()) {
        newErrors[field.name] = `${field.label} es obligatorio`;
      }
      if (field.maxLength && formData[field.name]?.length > field.maxLength) {
        newErrors[field.name] = `${field.label} no puede exceder ${field.maxLength} caracteres`;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await api.put(`${endpoint}/${id}`, formData);
      toast.success(`${title} actualizada con éxito`);
      setErrors({});
      navigate(backTo);
    } catch (error) {
      console.error(`Error al actualizar ${title}`, error);

      // Backend validation errors
      let backendErrors = {};
      if (error.response?.data?.errors) {
        error.response.data.errors.forEach((err) => {
          backendErrors[err.path || "general"] = err.msg;
        });
      } else if (error.response?.data?.error) {
        backendErrors.general = error.response.data.error;
      } else {
        backendErrors.general = "Ocurrió un error inesperado";
      }

      setErrors(backendErrors);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md max-w-xl mx-auto">
      <button
        onClick={() => navigate(backTo)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} />
        Volver a {title}s
      </button>

      <h2 className="text-3xl font-semibold text-gray-800 mb-6">
        Editar {title}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Error general */}
        {errors.general && (
          <p className="text-red-600 text-sm">{errors.general}</p>
        )}

        {fields.map((field) => (
          <div key={field.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            {field.type === "textarea" ? (
              <textarea
                name={field.name}
                value={formData[field.name] || ""}
                onChange={handleChange}
                placeholder={field.placeholder}
                className={`w-full border px-4 py-2 rounded-lg h-28 resize-none focus:outline-none focus:ring-2 ${
                  errors[field.name]
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-yellow-500"
                }`}
              ></textarea>
            ) : (
              <input
                type={field.type}
                name={field.name}
                value={formData[field.name] || ""}
                onChange={handleChange}
                placeholder={field.placeholder}
                className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                  errors[field.name]
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-yellow-500"
                }`}
              />
            )}
            {errors[field.name] && (
              <p className="text-red-600 text-sm mt-1">{errors[field.name]}</p>
            )}
          </div>
        ))}

        <button
          type="submit"
          className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-2 rounded-lg shadow transition duration-300"
        >
          <Pencil size={18} />
          Guardar Cambios
        </button>
      </form>
    </div>
  );
}

export default GenericEditForm;
